import React, { useState, useEffect } from "react";

interface ToastProps {
  message: string;
  isVisible: boolean;
  onUpdate?: () => void;
  onDismiss: () => void;
  type?: "info" | "success" | "update";
  duration?: number;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  isVisible,
  onUpdate,
  onDismiss,
  type = "info",
  duration = 5000,
}) => {
  useEffect(() => {
    if (isVisible && !onUpdate && duration > 0) {
      const timer = setTimeout(() => {
        onDismiss();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onUpdate, duration, onDismiss]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-20 pointer-events-auto"
        onClick={onDismiss}
      ></div>

      {/* Toast */}
      <div
        className={`
          pointer-events-auto
          relative
          max-w-sm w-full
          bg-white
          border border-gray-300
          rounded-md
          shadow-xl
          p-6
          transform transition-all duration-200 ease-in-out
          ${isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"}
        `}
      >
        <div className="text-center">
          <div className="mb-4">
            <p className="text-sm text-gray-800 leading-relaxed">{message}</p>
          </div>

          <div className="flex justify-center space-x-3">
            {onUpdate && (
              <button
                onClick={onUpdate}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded border-0 cursor-pointer transition-colors min-w-[70px]"
              >
                OK
              </button>
            )}
            <button
              onClick={onDismiss}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded border border-gray-300 cursor-pointer transition-colors min-w-[70px]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast Manager Hook
export const useToast = () => {
  const [toast, setToast] = useState<{
    message: string;
    type: "info" | "success" | "update";
    isVisible: boolean;
    onUpdate?: () => void;
  } | null>(null);

  const showToast = (
    message: string,
    type: "info" | "success" | "update" = "info",
    onUpdate?: () => void
  ) => {
    setToast({ message, type, isVisible: true, onUpdate });
  };

  const hideToast = () => {
    setToast((prev) => (prev ? { ...prev, isVisible: false } : null));
    setTimeout(() => setToast(null), 200); // Wait for animation
  };

  const ToastComponent = toast ? (
    <Toast
      message={toast.message}
      isVisible={toast.isVisible}
      type={toast.type}
      onUpdate={toast.onUpdate}
      onDismiss={hideToast}
      duration={toast.onUpdate ? 0 : 5000} // No auto-dismiss if update action exists
    />
  ) : null;

  return {
    showToast,
    hideToast,
    ToastComponent,
  };
};
